import { formatDateUTCToString } from '@/helpers/formatStringDate';
import { parseDateUtc } from '@/helpers/parseToUTC';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  useColorModeValue,
} from '@chakra-ui/react';
import { Field } from 'formik';
import type React from 'react';
import { useEffect, useState } from 'react';
import { safeDynamic } from '@/utils/safeDynamic';
import 'react-datepicker/dist/react-datepicker.css';

const areSameDates = (date1: Date | null, date2: Date | null): boolean => {
  if (!date1 || !date2) return false;
  return (
    date1.getUTCFullYear() === date2.getUTCFullYear() &&
    date1.getUTCMonth() === date2.getUTCMonth() &&
    date1.getUTCDate() === date2.getUTCDate()
  );
};

export interface DateRangePickFieldProps {
  id?: string;
  label?: string | any;
  placeholder?: string;
  name: string;
  errors?: string;
  touched?: boolean;
  isRequired?: boolean;
  full?: boolean;
  rightElement?: React.ReactNode;
  fontSize?: any;
  fontWeight?: string;
  italic?: boolean;
  isDisabled?: boolean;
  labelFontSize?: string;
  [x: string]: any;
}

const DatePicker = safeDynamic<any>(() => import('react-datepicker'), {
  ssr: false,
});

export function DateRangePickField(props: DateRangePickFieldProps) {
  const {
    id,
    label,
    placeholder,
    mb,
    isRequired,
    name,
    errors,
    touched,
    fontSize,
    fontWeight,
    italic,
    setFieldValue,
    value,
    isDisabled,
    labelFontSize,
    setTouched,
    onDark,
    ...rest
  } = props;

  const [lightColor, darkColor] = onDark
    ? ['white', 'chakra-body-text']
    : ['chakra-body-text', 'white'];

  const textColorPrimary = useColorModeValue(lightColor, darkColor);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    parseDateUtc(value?.startDate),
    parseDateUtc(value?.endDate),
  ]);
  const [startDate, endDate] = dateRange;

  useEffect(() => {
    const newStartDate = parseDateUtc(value?.startDate);
    const newEndDate = parseDateUtc(value?.endDate);

    if (!areSameDates(newStartDate, startDate) || !areSameDates(newEndDate, endDate)) {
      setDateRange([newStartDate, newEndDate]);
    }
  }, [value?.startDate, value?.endDate, startDate, endDate]);

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setDateRange(dates);

    const startUTC = start ? formatDateUTCToString(start, true) : null;
    const endUTC = end ? formatDateUTCToString(end, true) : null;

    if (startUTC && endUTC) {
      setFieldValue({ startDate: startUTC, endDate: endUTC });
    } else if (startUTC && !endUTC) {
      setFieldValue({ startDate: startUTC, endDate: null });
    } else if (!startUTC && !endUTC) {
      setFieldValue({ startDate: null, endDate: null });
    }
  };

  return (
    <Flex direction="column" mb={mb ?? '30px'} width="full" pos="relative">
      <FormControl
        isRequired={isRequired}
        isInvalid={!!errors}
        isDisabled={isDisabled}
        width="100%"
      >
        {label && (
          <FormLabel
            display="flex"
            htmlFor={id}
            fontSize={labelFontSize || '2xl'}
            lineHeight="normal"
            color={textColorPrimary}
            fontWeight="bold"
            mb={4}
            _hover={{ cursor: 'pointer' }}
          >
            {label}
          </FormLabel>
        )}
        <Box w="100%">
          <DatePicker
            selectsRange
            startDate={startDate || undefined}
            endDate={endDate || undefined}
            onChange={handleDateChange}
            onCalendarOpen={() => setTouched && setTouched(true)}
            readOnly={isDisabled}
            wrapperClassName={''}
            customInput={
              <Field
                as={Input}
                name={name}
                isDisabled={isDisabled}
                autoComplete="off"
                placeholder={placeholder}
                fontWeight={fontWeight ?? '500'}
                color={textColorPrimary}
                fontSize={fontSize ?? '20px'}
                border="3px solid"
                borderColor={isDisabled ? 'gray' : 'white'}
                borderRadius="10px"
                bg="blue.800"
                h="60px"
                maxH="60px"
                style={{ fontStyle: italic ? 'italic' : 'normal', color: textColorPrimary }}
                _placeholder={{ color: textColorPrimary }}
                {...rest}
              />
            }
          />
        </Box>
        {errors && touched && <FormErrorMessage ml="10px">This field is required</FormErrorMessage>}
      </FormControl>
    </Flex>
  );
}
